import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { waitForJob } from '@/api/jobs';
import { Button, ConfirmHeader, KeyValueList, Modal, ModalFooter, useModalTitleId, useToast } from '@/design-system';
import { errorText } from '@/shell/errorText';
import { orgStoreQuery, refreshStore, syncStore, type ImportResult } from './api';
import s from './store.module.css';

/**
 * «Отправить товары в Laravel Store» (DESIGN_SPEC §4.12; TZ §6.3, AC-03): opening runs the dry run of the
 * selection (`POST /admin/websites/{id}/sync-store {dry_run: true}`), whose preview fills the rows; the
 * confirm imports the same selection (`dry_run: false`) and the result's counters come back as the toast.
 */
export function ImportModal({ siteId, orgId, productIds, open, onClose }: { siteId: string; orgId: string; productIds?: string[]; open: boolean; onClose: () => void }) {
  const { t } = useTranslation('store');
  const { t: tc } = useTranslation('common');
  const titleId = useModalTitleId();
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const store = useQuery({ ...orgStoreQuery(orgId), enabled: open });
  const selection = productIds?.length ? { product_ids: productIds } : {};
  const dry = useQuery({
    queryKey: ['store', 'dry-run', siteId, productIds ?? null],
    queryFn: () => syncStore(siteId, { dry_run: true, ...selection }),
    enabled: open,
    retry: false,
    staleTime: 0,
    gcTime: 0,
  });
  const preview = dry.data?.preview;
  const dash = tc('dash');
  const count = preview?.counts.products ?? 0;
  const confirm = () => {
    if (busy || !preview) return;
    setBusy(true);
    syncStore(siteId, { dry_run: false, ...selection })
      .then((r) => {
        toast(t('import.started'));
        onClose();
        refreshStore(qc, siteId, orgId);
        return waitForJob(r.job_id).then((result) => {
          const res = result as Partial<ImportResult>;
          toast(t('import.done', { created: res.created ?? 0, updated: res.updated ?? 0, skipped: res.skipped ?? 0, errors: res.errors?.length ?? 0 }));
          refreshStore(qc, siteId, orgId);
        });
      })
      .catch((e: unknown) => toast(errorText(e, tc)))
      .finally(() => setBusy(false));
  };
  const rows = [
    { key: 'approved', label: t('import.approved'), value: preview ? String(preview.counts.products) : dash },
    { key: 'categories', label: t('import.categories'), value: preview ? t('import.categories_value', { count: preview.counts.categories }) : dash },
    { key: 'status', label: t('import.status'), value: t('import.status_value') },
    { key: 'media', label: t('import.media'), value: t('import.media_value') },
  ];
  if (preview?.flags.price_unverified) rows.push({ key: 'unverified', label: t('import.unverified'), value: String(preview.flags.price_unverified) });
  if (preview?.flags.image_dropped) rows.push({ key: 'images', label: t('import.images_dropped'), value: String(preview.flags.image_dropped) });
  return (
    <Modal open={open} onClose={onClose} labelledBy={titleId}>
      <ConfirmHeader id={titleId} title={t('import.title')} text={t('import.text', { store: store.data?.external_store_id ?? dash })} />
      <KeyValueList rows={rows} />
      {dry.isError && (
        <span role="alert" className={s.importError}>
          {errorText(dry.error, tc)}
        </span>
      )}
      <ModalFooter>
        <Button variant="secondary" size={46} onClick={onClose}>
          {tc('actions.cancel')}
        </Button>
        <Button size={46} aria-busy={busy} disabled={!preview || count === 0} onClick={confirm}>
          {t('import.submit', { count })}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
